import React from 'react';
import PropTypes from 'prop-types';
import {
  CAN_APPROVE_LEAD_VAULT_ACCESS_TYPES,
  CAN_REVOKE_LEAD_VAULT_ACCESS_TYPES,
  CAN_UNBLOCK_LEAD_VAULT_ACCESS_TYPES
} from '../../reducers/leads';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap-5';
import { faUserCog } from '@fortawesome/pro-solid-svg-icons';

const AccessOptions = ({
  iconClassName,
  lead,
  currentUser,
  approveLeadVaultAccess,
  refreshLead,
  revokeLeadVaultAccess,
  blockLeadVaultAccess,
  unblockLeadVaultAccess,
}) => {
  const accessOptions = [];

  (lead.propertyVaultProfiles || []).forEach((propertyVaultProfile) => {
    const { id, inWaitlist, approvalState, approveAccessPath, revokeAccessPath, saleOrLease } = propertyVaultProfile;

    if (inWaitlist && CAN_APPROVE_LEAD_VAULT_ACCESS_TYPES.includes(approvalState)) {
      accessOptions.push(
        <Dropdown.Item
          key={`approve-${id}`}
          name={`approveVaultAccess${id}`}
          onClick={() => {
            approveLeadVaultAccess(lead.id, id, approveAccessPath).then(() => {
              refreshLead(lead);
            });
            document.body.click();
          }}
        >
          <FontAwesomeIcon className={iconClassName} fixedWidth icon={faUserCog} size="sm"/>
          Approve {saleOrLease} Access
        </Dropdown.Item>
      );
    }

    if (inWaitlist && CAN_REVOKE_LEAD_VAULT_ACCESS_TYPES.includes(approvalState)) {
      accessOptions.push(
        <Dropdown.Item
          key={`revoke-${id}`}
          name={`revokeVaultAccess${id}`}
          onClick={() => {
            revokeLeadVaultAccess(lead.id, id, revokeAccessPath).then(() => {
              refreshLead(lead);
            });
            document.body.click();
          }}
        >
          <FontAwesomeIcon className={iconClassName}
            fixedWidth icon={faUserCog} size="sm"
          /> Revoke {saleOrLease} Access
        </Dropdown.Item>
      );
    }
  });

  const profileId = lead.profileId;

  if (currentUser.canManageCompany && !CAN_UNBLOCK_LEAD_VAULT_ACCESS_TYPES.includes(lead.approvalStatus)) {
    const blockIconClassName = profileId ? iconClassName : iconClassName.replace('text-primary', 'text-muted');

    const menuItem = (
      <Dropdown.Item
        disabled={!profileId}
        name="blockVaultAccess"
        onSelect={() => {
          blockLeadVaultAccess(lead.id, lead.projectId).then(() => {
            refreshLead(lead);
          });
          document.body.click();
        }}
      >
        <FontAwesomeIcon className={blockIconClassName} fixedWidth icon={faUserCog} size="sm"/> Block Access
      </Dropdown.Item>
    );

    if (profileId) {
      accessOptions.push(<React.Fragment key={`block-${profileId}`}>{menuItem}</React.Fragment>);
    } else {
      const tooltip = (
        <Tooltip id={`tooltip-block-${profileId}`}>
          this lead doesn't have a buildout profile so they're unable to be blocked
        </Tooltip>
      );

      accessOptions.push(<OverlayTrigger key={`block-${profileId}`} overlay={tooltip} placement="left">
        {menuItem}
      </OverlayTrigger>);
    }
  } else if (currentUser.canManageCompany && CAN_UNBLOCK_LEAD_VAULT_ACCESS_TYPES.includes(lead.approvalStatus)) {
    accessOptions.push(
      <Dropdown.Item
        key="unblock"
        name="unblockVaultAccess"
        onClick={() => {
          unblockLeadVaultAccess(lead.id, lead.projectId).then(() => {
            refreshLead(lead);
          });
          document.body.click();
        }}
      >
        <FontAwesomeIcon className={iconClassName} fixedWidth icon={faUserCog} size="sm"/> Unblock Access
      </Dropdown.Item>
    );
  }

  return <React.Fragment>{accessOptions}</React.Fragment>;
};

AccessOptions.defaultProps = {
  iconClassName: 'me-2 text-primary'
};

AccessOptions.propTypes = {
  approveLeadVaultAccess: PropTypes.func.isRequired,
  blockLeadVaultAccess: PropTypes.func.isRequired,
  currentUser: PropTypes.shape({
    canManageCompany: PropTypes.bool.isRequired
  }).isRequired,
  iconClassName: PropTypes.string,
  lead: PropTypes.object.isRequired,
  refreshLead: PropTypes.func.isRequired,
  revokeLeadVaultAccess: PropTypes.func.isRequired,
  unblockLeadVaultAccess: PropTypes.func.isRequired
};

export default AccessOptions;
